import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, CartIcon, AlertIcon, LeafIcon, BoneIcon } from './Icons';
interface BonusCardProps {
  number: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  reverse?: boolean;
}
function BonusCard({
  number,
  title,
  description,
  icon,
  reverse
}: BonusCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.6
  }} className={`flex flex-col sm:flex-row items-start gap-5 sm:gap-6 ${reverse ? 'sm:flex-row-reverse' : ''}`} data-id="element-1">
      {/* Icon */}
      <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-bg-card border border-white/5 flex items-center justify-center shadow-lg" data-id="element-2">
        {icon}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0" data-id="element-3">
        <span className="inline-block font-sans text-[10px] uppercase tracking-widest text-gold/60 font-semibold mb-1.5" data-id="element-4">
          Bonus #{number}
        </span>
        <h3 className="font-serif text-xl sm:text-2xl font-bold text-cream leading-snug mb-2" data-id="element-5">
          {title}
        </h3>
        <p className="font-sans text-sm sm:text-base text-cream-muted leading-relaxed" data-id="element-6">
          {description}
        </p>
      </div>
    </motion.div>;
}
export function BonusPreviews() {
  return <section className="relative w-full py-20 sm:py-28 overflow-hidden" style={{
    background: 'linear-gradient(180deg, #111a11 0%, #0d160d 50%, #111a11 100%)'
  }} aria-label="Bonus previews" data-id="element-7">
      <div className="max-w-3xl mx-auto px-5 sm:px-8" data-id="element-8">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-9">
          <p className="font-sans text-xs sm:text-sm tracking-[0.2em] uppercase text-gold/70 mb-3" data-id="element-10">
            Included Free
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-cream" data-id="element-11">
            5 Bonuses to Accelerate Results
          </h2>
        </motion.div>

        {/* Bonus cards */}
        <div className="space-y-12 sm:space-y-16" data-id="element-12">
          <BonusCard number={1} title="The 15-Minute Meal Prep Guide" description="One 15-minute Sunday session. Seven days of longevity bowls ready to go. No cooking skills needed — just chop, portion, and store." icon={<ClockIcon className="w-7 h-7 text-gold" data-id="element-14" />} data-id="element-13" />

          <div className="max-w-32 mx-auto h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" aria-hidden="true" data-id="element-15" />

          <BonusCard number={2} title="The Supermarket Cheat Sheet" description="The exact aisles, brands, and products to grab at Costco, Walmart, or Aldi. Walk in with a list, walk out with everything your dog needs for pennies per serving." icon={<CartIcon className="w-7 h-7 text-gold" data-id="element-17" />} reverse data-id="element-16" />

          <div className="max-w-32 mx-auto h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" aria-hidden="true" data-id="element-18" />

          <BonusCard number={3} title='The "Poison List" (Hidden Toxins)' description="The ingredients hiding in plain sight on your kibble bag that are silently fueling inflammation, allergies, and joint breakdown. Know exactly what to avoid." icon={<AlertIcon className="w-7 h-7 text-urgency-red-bright" data-id="element-20" />} data-id="element-19" />

          <div className="max-w-32 mx-auto h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" aria-hidden="true" data-id="element-21" />

          <BonusCard number={4} title='The "Itchy Dog" Protocol' description="A targeted plan to calm chronic scratching, hot spots, and skin irritation using specific anti-inflammatory toppers. Most owners see results within 2 weeks." icon={<LeafIcon className="w-7 h-7 text-green-accent" data-id="element-23" />} reverse data-id="element-22" />

          <div className="max-w-32 mx-auto h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" aria-hidden="true" data-id="element-24" />

          <BonusCard number={5} title="Giant Breed Joint Rescue" description="The specific nutrients, portions, and timing to support joints under 100+ lbs of pressure. Designed for Cane Corsos, Mastiffs, Great Danes, and other giants." icon={<BoneIcon className="w-7 h-7 text-gold" data-id="element-26" />} data-id="element-25" />
        </div>

        {/* Bottom divider */}
        <div className="mt-20 max-w-24 mx-auto h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" aria-hidden="true" data-id="element-27" />
      </div>
    </section>;
}